import { Dispatch } from '@reduxjs/toolkit';
import axios from 'axios';
import { v4 as uuidV4 } from 'uuid';
import { setHideLoader, setShowLoader } from '../../storeSlices/generalSlice';
import { setIsAuthenticated } from '../../storeSlices/userSlice';
import { PathsConfig, PATH_TO_CONFIG } from './configPaths';
import { keepAliveIds, startKeepAlive, stopKeepAliveTime } from '../keepAliveService/keepAliveService';
import { clearAuthToken, getAuthToken } from '../authToken/authTokenService';
import { endFullstorySession } from '../fullstory/fullstoryService';

interface httpServiceProps {
	path: PathsConfig;
	data?: any;
	params?: any;
	urlParam?: Record<string, string | number>;
	showLoader?: boolean;
	responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
	unResetKeepAlive?: boolean;
	dispatch: Dispatch;
}

// Lazy initialization to ensure window._env_ is set
let instance: ReturnType<typeof axios.create> | null = null;

const getInstance = () => {
	if (!instance) {
		instance = axios.create({
			baseURL: window._env_?.API_ENDPOINT || '',
			timeout: window._env_?.API_TIMEOUT ? parseInt(window._env_.API_TIMEOUT) : 120000,
			headers: { 'X-Custom-Header': 'foobar' },
			withCredentials: true,
		});
	}
	return instance;
};

export const setupInterceptor = (dispatch: Dispatch) => {
	const axiosInstance = getInstance();
	axiosInstance.interceptors.request.use(
		(req) => {
			// Embedded (iframe) flow: the session cookie is dropped cross-origin, so replay
			// the exchanged JWT as a Bearer header. Empty in normal cookie-based sessions.
			const token = getAuthToken();
			if (token && req.headers) {
				req.headers.Authorization = `Bearer ${token}`;
			}
			return req;
		},
		(err) => {
			dispatch(setHideLoader(null));
			return Promise.reject(err);
		},
	);

	axiosInstance.interceptors.response.use(
		(res) => {
			return Promise.resolve(res);
		},
		(err) => {
			dispatch(setHideLoader(null));

			if (err.response?.status === 401 || err.response?.status === 403) {
				clearAuthToken();
				endFullstorySession();
				dispatch(setIsAuthenticated(false));
			}
			return Promise.reject(err);
		},
	);
};

export const getUrlWithParams = (url: string, params: Record<string, string | number> = {}) => {
	return Object.entries(params).reduce((urlWithParams, [key, value]) => {
		return urlWithParams.replace(`<${key}>`, String(value));
	}, url);
};

export const httpService = async ({
	path,
	data,
	params,
	urlParam,
	dispatch,
	responseType,
	showLoader = true,
	unResetKeepAlive,
}: httpServiceProps) => {
	if (!unResetKeepAlive) {
		stopKeepAliveTime();
		if (keepAliveIds.length === 0) {
			startKeepAlive(dispatch);
		}
	}

	const loaderId = uuidV4();
	showLoader && dispatch(setShowLoader(loaderId));

	const { method, url } = PATH_TO_CONFIG[path];
	const urlWithParams = getUrlWithParams(url, urlParam);

	const LoaderTimeout = window._env_?.API_TIMEOUT ? +window._env_.API_TIMEOUT : 120000;
	setTimeout(() => {
		dispatch(setHideLoader(loaderId));
	}, LoaderTimeout);

	const axiosInstance = getInstance();
	const res = await axiosInstance({ method, url: urlWithParams, data, params, headers: { 'X-Request-ID': uuidV4() }, responseType });
	showLoader && dispatch(setHideLoader(loaderId));
	return res;
};
